import { useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK } from "@/lib/app";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ZAxis, Cell } from "recharts";
import { Warning, TrendUp, TrendDown, Calendar } from "@phosphor-icons/react";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";

const fetcher = (url) => api.get(url).then((r) => r.data);

const TYPE_STYLE = {
  high: { color: "var(--success)", bg: "var(--success-bg)", label: "Vysoká tržba", Icon: TrendUp },
  low: { color: "var(--critical)", bg: "var(--critical-bg)", label: "Nízka tržba", Icon: TrendDown },
  holiday: { color: "var(--warning)", bg: "var(--warning-bg)", label: "Sviatok", Icon: Calendar },
};

export default function Anomalies({ storeId, period }) {
  const [localPeriod, setLocalPeriod] = useState(period);
  const [threshold, setThreshold] = useState(1.8);
  const qs = `period_from=${localPeriod?.from || ""}&period_to=${localPeriod?.to || ""}&threshold=${threshold}`;
  const { data } = useSWR(`/stores/${storeId}/analytics/anomalies?${qs}`, fetcher);

  if (!data) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie anomálií" testIdPrefix="anomalies-period" />
      <div className="text-[var(--text-secondary)]">Načítavam…</div>
    </div>
  );
  if (data.empty) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie anomálií" testIdPrefix="anomalies-period" />
      <div className="card-flat p-8 text-center text-[var(--text-secondary)]">V tomto období nie sú dáta.</div>
    </div>
  );

  const high = data.anomalies.filter((a) => a.type === "high").length;
  const low = data.anomalies.filter((a) => a.type === "low").length;
  const hol = data.anomalies.filter((a) => a.type === "holiday").length;

  // build scatter points: x = weekday, y = revenue, color by type
  const points = data.anomalies.map((a) => ({
    x: a.weekday,
    y: a.revenue,
    z: Math.abs(a.z_score),
    a,
  }));

  return (
    <div className="space-y-5">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie anomálií" testIdPrefix="anomalies-period" />
      <div className="card-flat p-4 flex flex-wrap items-center gap-4">
        <div>
          <p className="label-eyebrow mb-1">Citlivosť (z-score prah)</p>
          <input
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            data-testid="anomaly-threshold"
            className="w-48"
          />
          <span className="tabular text-sm ml-2 text-[var(--text-secondary)]">{threshold.toFixed(1)}σ</span>
        </div>
        <p className="text-xs text-[var(--text-muted)] ml-auto max-w-md">
          Tržba sa porovnáva s priemerom rovnakých dní v týždni. Body, ktoré sa odchýlia o viac ako prah σ (alebo sú štátnym sviatkom) sú anomálie.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Spolu dní</p>
          <p className="font-display text-3xl font-bold tabular">{data.total_days}</p>
        </div>
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Vysoké tržby</p>
          <p className="font-display text-3xl font-bold tabular text-[var(--success)]">{high}</p>
        </div>
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Nízke tržby</p>
          <p className="font-display text-3xl font-bold tabular text-[var(--critical)]">{low}</p>
        </div>
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Sviatky</p>
          <p className="font-display text-3xl font-bold tabular text-[var(--warning)]">{hol}</p>
        </div>
      </div>

      <div className="card-flat p-5">
        <h3 className="font-display text-base font-semibold mb-1">Anomálie podľa dňa v týždni</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-4">Veľkosť bodu = sila odchýlky (|z|). Hover pre detail.</p>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              domain={[-0.5, 6.5]}
              ticks={[0, 1, 2, 3, 4, 5, 6]}
              tickFormatter={(v) => WEEKDAYS_SK[v]?.slice(0, 2) || ""}
              tick={{ fontSize: 11, fill: "#5E6962" }}
            />
            <YAxis type="number" dataKey="y" tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
            <ZAxis type="number" dataKey="z" range={[60, 400]} />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={({ payload }) => {
                if (!payload?.length) return null;
                const a = payload[0].payload.a;
                return (
                  <div className="bg-white border border-[var(--border-default)] rounded p-2 text-xs shadow">
                    <div className="font-semibold tabular">{a.date} · {a.weekday_name}</div>
                    <div className="tabular mt-1">Tržba: {fmtEur(a.revenue)} (ø {fmtEur(a.expected_revenue)})</div>
                    <div className="tabular">z-score: {a.z_score}</div>
                    {a.holiday_name && <div className="text-[var(--warning)] mt-1">{a.holiday_name}</div>}
                  </div>
                );
              }}
            />
            <Scatter data={points} isAnimationActive={false}>
              {points.map((p, i) => {
                const c = p.a.type === "high" ? "#3E7B52" : p.a.type === "low" ? "#BA3F2B" : "#C78220";
                return <Cell key={i} fill={c} />;
              })}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="font-display text-sm font-semibold">Zoznam anomálnych dní</h3>
        </div>
        <div className="overflow-x-auto scroll-area">
          <table>
            <thead>
              <tr>
                <th>Dátum</th>
                <th>Deň</th>
                <th>Typ</th>
                <th className="text-right">Tržba</th>
                <th className="text-right">Očakávaná ø</th>
                <th className="text-right">Δ</th>
                <th className="text-right">z-score</th>
                <th>Dôvod</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.anomalies.map((a) => {
                const style = TYPE_STYLE[a.type] || TYPE_STYLE.high;
                const Icon = style.Icon;
                const delta = a.revenue - a.expected_revenue;
                return (
                  <tr key={a.date}>
                    <td className="tabular">{a.date}</td>
                    <td>{a.weekday_name}</td>
                    <td>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: style.bg, color: style.color }}>
                        <Icon size={12} weight="bold" /> {style.label}
                      </span>
                    </td>
                    <td className="text-right tabular">{fmtEur(a.revenue)}</td>
                    <td className="text-right tabular text-[var(--text-secondary)]">{fmtEur(a.expected_revenue)}</td>
                    <td className={`text-right tabular ${delta > 0 ? "text-[var(--success)]" : "text-[var(--critical)]"}`}>
                      {delta > 0 ? "+" : ""}{fmtEur(delta)}
                    </td>
                    <td className="text-right tabular">{a.z_score >= 0 ? "+" : ""}{fmtNum(a.z_score, 2)}</td>
                    <td className="text-xs text-[var(--text-secondary)]">{a.reasons.join(" · ")}</td>
                    <td className="text-right">
                      <PinButton
                        storeId={storeId}
                        kind="anomaly"
                        label={`Anomália ${a.date} (${a.weekday_name}) — ${style.label}: tržba ${a.revenue} € (ø ${a.expected_revenue} €, z=${a.z_score})`}
                        data={a}
                        period={period}
                        testId={`pin-anomaly-${a.date}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {data.anomalies.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-[var(--text-secondary)] py-6">
                    <Warning size={20} className="mx-auto mb-2 text-[var(--text-muted)]" /> Žiadne anomálie pri tomto prahu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
